"use client";

// Inspired by react-hot-toast library
import * as React from "react";

const TOAST_REMOVE_DELAY = 5000; // 5 seconds auto-dismiss
const MAX_TOASTS = 3; // Maximum number of toasts to show at once

const ACTION_TYPES = {
  ADD_TOAST: "ADD_TOAST",
  UPDATE_TOAST: "UPDATE_TOAST",
  DISMISS_TOAST: "DISMISS_TOAST",
  REMOVE_TOAST: "REMOVE_TOAST",
} as const;

export interface ToastProps {
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  variant?: "default" | "destructive" | "success";
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export interface Toast extends ToastProps {
  id: string;
}

type Action =
  | {
      type: typeof ACTION_TYPES.ADD_TOAST;
      toast: Toast;
    }
  | {
      type: typeof ACTION_TYPES.UPDATE_TOAST;
      toast: Partial<Toast>;
    }
  | {
      type: typeof ACTION_TYPES.DISMISS_TOAST;
      toastId?: string;
    }
  | {
      type: typeof ACTION_TYPES.REMOVE_TOAST;
      toastId?: string;
    };

interface State {
  toasts: Toast[];
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

const addToRemoveQueue = (toastId: string) => {
  if (toastTimeouts.has(toastId)) {
    return;
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId);
    dispatch({
      type: ACTION_TYPES.REMOVE_TOAST,
      toastId: toastId,
    });
  }, TOAST_REMOVE_DELAY);

  toastTimeouts.set(toastId, timeout);
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case ACTION_TYPES.ADD_TOAST:
      return {
        ...state,
        toasts: [...state.toasts, action.toast],
      };

    case ACTION_TYPES.UPDATE_TOAST:
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t
        ),
      };

    case ACTION_TYPES.DISMISS_TOAST: {
      const { toastId } = action;

      if (toastId) {
        toastTimeouts.forEach((_, id) => {
          if (id === toastId) {
            toastTimeouts.delete(id);
          }
        });

        return {
          ...state,
          toasts: state.toasts.map((t) =>
            t.id === toastId ? { ...t, open: false } : t
          ),
        };
      }

      return {
        ...state,
        toasts: state.toasts.map((t) => ({ ...t, open: false })),
      };
    }

    case ACTION_TYPES.REMOVE_TOAST:
      if (action.toastId === undefined) {
        return {
          ...state,
          toasts: [],
        };
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      };
  }
};

const listeners: Array<(state: State) => void> = [];

let memoryState: State = { toasts: [] };

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action);
  listeners.forEach((listener) => {
    listener(memoryState);
  });
}

function toast({ ...props }: ToastProps) {
  if (typeof window === "undefined") return;

  // Check for existing toast with the same title and variant to prevent duplicates
  const existingToast = memoryState.toasts.find(
    (t) => t.title === props.title && t.variant === props.variant && t.open
  );
  
  if (existingToast) {
    // If a toast with the same title and variant already exists, don't create a new one
    return {
      id: existingToast.id,
      dismiss: () => dispatch({ type: ACTION_TYPES.DISMISS_TOAST, toastId: existingToast.id }),
      update: (newProps: ToastProps) =>
        dispatch({
          type: ACTION_TYPES.UPDATE_TOAST,
          toast: { ...newProps, id: existingToast.id },
        }),
    };
  }

  // Remove oldest toasts if we've reached the maximum
  const openToasts = memoryState.toasts.filter((t) => t.open);
  if (openToasts.length >= MAX_TOASTS) {
    // Dismiss the oldest toast(s) to make room
    const toastsToRemove = openToasts.slice(0, openToasts.length - MAX_TOASTS + 1);
    toastsToRemove.forEach((t) => {
      dispatch({ type: ACTION_TYPES.DISMISS_TOAST, toastId: t.id });
    });
  }

  const id = Math.random().toString(36).substring(2, 9);

  const update = (props: ToastProps) =>
    dispatch({
      type: ACTION_TYPES.UPDATE_TOAST,
      toast: { ...props, id },
    });
  const dismiss = () =>
    dispatch({ type: ACTION_TYPES.DISMISS_TOAST, toastId: id });

  dispatch({
    type: ACTION_TYPES.ADD_TOAST,
    toast: {
      ...props,
      id,
      open: true,
      onOpenChange: (open: boolean) => {
        if (!open) dismiss();
      },
    },
  });

  // Add to remove queue for auto-dismiss
  addToRemoveQueue(id);

  return {
    id,
    dismiss,
    update,
  };
}

function useToast() {
  const [state, setState] = React.useState<State>(memoryState);

  React.useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, [state]);

  return {
    ...state,
    toast,
    dismiss: (toastId?: string) =>
      dispatch({ type: ACTION_TYPES.DISMISS_TOAST, toastId }),
  };
}

export { toast, useToast };
